import { Alerts } from 'constants/alerts';
import { BankOperationStatusesNew } from 'constants/bankOperationStatusesNew';
import { EMPTY_FIELD } from 'constants/emptyField';
import { Resources } from 'constants/resources';
import { Role } from 'constants/roles';

import CommentIcon from '@mui/icons-material/Comment';
import ModeCommentOutlinedIcon from '@mui/icons-material/ModeCommentOutlined';
import { useApiContext } from 'context/apiContext';
import { usePermissions } from 'hooks/usePermissions';
import { BankOperationsFinanceUserAlert } from 'pages/Finance/BankOperations/components/BankOperationsFinanceUserAlert';
import { BankOperationsHeaderRow } from 'pages/Finance/BankOperations/components/BankOperationsHeaderRow';
import { BankOperationRow } from 'pages/Finance/BankOperations/components/BankOperationsRow';
import { BankOperationStatusChipField } from 'pages/Finance/BankOperations/components/BankOperationStatusChipField/BankOperationStatusChipField';
import { RecordContextProvider, useListContext } from 'react-admin';
import { Alert } from 'shared/mui/Alert/Alert';
import {
  ContextMenu,
  ContextMenuEditDialogButton,
  ContextMenuShowButton,
} from 'shared/mui/ContextMenu';
import { Typography } from 'shared/mui/Typography';
import { DatagridBody } from 'shared/react-admin/DatagridBodies/DatagridBody/DatagridBody';
import {
  DateTimeField,
  EmbeddedFunctionField,
  FunctionField,
  TextLinkField,
  TooltipTextField,
  UserProfileField,
} from 'shared/react-admin/Fields';
import { IconButtonStyled } from 'shared/react-admin/Fields/CommissionStatusChipField/CommissionStatusChipField.styled';
import { ReferenceField } from 'shared/react-admin/ReferenceFields';
import { BankOperation } from 'types/embedded/bankOperation';
import { formatCurrency } from 'utils';

import { DatagridStyled } from './BankOperationsDatagrid.styled';

type Props = {
  handleOpenMerchantAccountModal: (id?: string) => void;
  handleOpenRecordModalComment: (record: BankOperation) => void;
  handleOpenRecordModalEditComment: (record: BankOperation) => void;
};

export const BankOperationsDatagrid: React.FC<Props> = ({
  handleOpenMerchantAccountModal,
  handleOpenRecordModalComment,
  handleOpenRecordModalEditComment,
}) => {
  const { hasResourceAccess, isHaveRole } = usePermissions();

  const { total, filterValues } = useListContext();

  const { user } = useApiContext();
  const showEmptyListAlert = Boolean(!total);

  if (user.alerts.includes(Alerts.EmptyProviderAccountAccess)) {
    return (
      <BankOperationsFinanceUserAlert userLastName={user.lastName} userName={user.firstName} />
    );
  }

  if (showEmptyListAlert) {
    return (
      <Alert severity="info">
        Нет созданных “Банковских операций” для доступных “Мерчантов” и “Провайдеров”
      </Alert>
    );
  }

  return (
    <DatagridStyled
      body={<DatagridBody row={<BankOperationRow />} />}
      bulkActionButtons={false}
      header={<BankOperationsHeaderRow />}
      resizableColumns
    >
      <TextLinkField
        cellClassName="resize-originId"
        headerClassName="resize-originId"
        label="ID"
        resource={Resources.BankOperations}
        sortBy="id"
        source="originId"
        type="show"
      />
      <DateTimeField
        cellClassName="resize-dateOfOperation"
        dateMask="dd.MM.yyyy"
        headerClassName="resize-dateOfOperation"
        label="Дата в ПС"
        source="dateOfOperation"
        timeMask={false}
        timezone={filterValues.timezone}
      />
      <EmbeddedFunctionField
        cellClassName="resize-type"
        headerClassName="resize-type"
        label="Тип операции"
        sourceField="type.description"
      />
      <EmbeddedFunctionField
        cellClassName="resize-direction"
        headerClassName="resize-direction"
        label="Направление"
        sourceField="direction.description"
      />
      <FunctionField
        cellClassName="resize-amountPenny"
        headerClassName="resize-amountPenny"
        label="Сумма"
        render={({ amountPenny }: BankOperation) => {
          return formatCurrency(Number(amountPenny));
        }}
        sortable={false}
        source="amountPenny"
      />
      {hasResourceAccess(Resources.Currencies) && (
        <EmbeddedFunctionField
          cellClassName="resize-currency"
          headerClassName="resize-currency"
          label="Валюта"
          render={(value) => (
            <Typography fontWeight={500} variant="body2">
              {value}
            </Typography>
          )}
          sourceField="currency.alphaCode"
        />
      )}
      <FunctionField
        cellClassName="resize-status"
        headerClassName="resize-status"
        label="Статус"
        render={(record: BankOperation) => {
          return (
            <RecordContextProvider value={record?.status}>
              <BankOperationStatusChipField source="description">
                {Boolean(record?.commentCount) && (
                  <IconButtonStyled onClick={() => handleOpenRecordModalComment(record)}>
                    <ModeCommentOutlinedIcon />
                  </IconButtonStyled>
                )}
              </BankOperationStatusChipField>
            </RecordContextProvider>
          );
        }}
      />
      {hasResourceAccess('referrals') && (
        <EmbeddedFunctionField
          cellClassName="resize-referral"
          headerClassName="resize-referral"
          label="Реферал"
          sortable={false}
          sourceField="referral.name"
        />
      )}
      {hasResourceAccess('merchants') && (
        <EmbeddedFunctionField
          cellClassName="resize-merchant"
          headerClassName="resize-merchant"
          label="Мерчант"
          sortable={false}
          sourceField="merchant.name"
        />
      )}
      {hasResourceAccess(Resources.Providers) && (
        <EmbeddedFunctionField
          cellClassName="resize-provider"
          headerClassName="resize-provider"
          label="Провайдер"
          sortable={false}
          sourceField="provider.name"
        />
      )}
      <FunctionField
        cellClassName="resize-merchantAccount"
        headerClassName="resize-merchantAccount"
        label="Счет мерчанта"
        render={(record: BankOperation) => (
          <Typography
            color="secondary"
            onClick={() => handleOpenMerchantAccountModal(record.merchantAccount?.['@id'])}
            sx={{
              color: 'primary.main',
              cursor: 'pointer',
              textDecoration: 'underline',
              textWrap: 'nowrap',
            }}
            variant="body2"
          >
            {record.merchantAccount?.name}
          </Typography>
        )}
        sortable={false}
        source="merchantAccount.name"
      />
      <TooltipTextField
        cellClassName="resize-description"
        headerClassName="resize-description"
        label="Комментарий"
        source="description"
        sx={{
          display: 'block',
          overflow: 'hidden',
          textDecoration: 'none',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
        }}
      />
      <ReferenceField
        cellClassName="resize-approver"
        empty={EMPTY_FIELD}
        headerClassName="resize-approver"
        label="Согласующий"
        link={false}
        reference="users"
        sortable={false}
        source="approver.@id"
      >
        <FunctionField render={() => <UserProfileField />} />
      </ReferenceField>
      <DateTimeField
        cellClassName="resize-approveAt"
        headerClassName="resize-approveAt"
        label="Дата согласования"
        sortable={false}
        source="approveAt"
        timezone={filterValues.timezone}
      />
      <DateTimeField
        cellClassName="resize-canceledAt"
        headerClassName="resize-canceledAt"
        label="Дата аннулирования"
        sortable={false}
        source="canceledAt"
        timezone={filterValues.timezone}
      />
      <ContextMenu
        render={(record: BankOperation) => {
          return [
            <ContextMenuShowButton
              key="show-button"
              linkProps={{ resource: Resources.BankOperations }}
            />,
            isHaveRole([Role.FinanceLead, Role.Admin]) &&
              record?.status?.value !== BankOperationStatusesNew.Canceled && (
                <ContextMenuEditDialogButton
                  icon={<CommentIcon fontSize="small" />}
                  key="edit-button"
                  label="Редактировать комментарий"
                  onClick={() => handleOpenRecordModalEditComment(record)}
                />
              ),
          ];
        }}
      />
    </DatagridStyled>
  );
};
